import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Container,
  Flex,
  Heading,
  Link,
  SimpleGrid,
  Text,
  Grid,
  GridItem,
  Button,
} from '@chakra-ui/react';
import CampaignBanner from 'components/campaigns/CampaignBanner';
import { HeadMeta } from 'components/HeadMeta';
import { getSharedPageProps, SharedPageProps } from 'components/Layout';
import ProductCard from 'components/product/ProductCard';
import { colors } from 'constants/constants';

import {
  GetFacilities,
  GetFacility as GetFacilityCore,
  GetAvailableProductsForFacility,
} from 'helpers/coreApi';
import {
  BlockRenderer,
  GetFacility as GetFacilityUmbraco,
} from 'helpers/umbracoApi';
import { GetStaticPaths, GetStaticProps } from 'next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { Discount, FacilityDto, ProductDto } from 'types/generic';
import { FacilityContent } from 'types/umbraco';
interface FacilityProps {
  facility: FacilityDto;
  products: ProductDto[];
  facilitySlug: string;
  facilityContent: FacilityContent;
  sharedPageProps: SharedPageProps;
}

function FacilityInfo({ content }: { content: FacilityContent }): JSX.Element {
  const [showMap, setShowMap] = useState<boolean>(false);

  if (!content) {
    return null;
  }
  return (
    <>
      <HeadMeta {...content.meta} />
      <Box p={{ base: '0 2.5%', md: '0 5%', xl: '0 0' }}>
        <Container maxW="7xl">
          <Grid
            templateColumns={{ base: '1fr', md: '7fr 5fr' }}
            mt={{ base: '2em', md: '6em' }}>
            <GridItem>
              <Flex
                flex={{ base: 1, md: 3 }}
                flexDir="column"
                mr={{ base: 0, md: '5em' }}>
                <Box>
                  <Text mt={4} mb={8} size="md">
                    {content?.preamble?.replace(/<[^>]*>?/gm, '')}
                  </Text>
                </Box>
                <SimpleGrid columns={2} spacingX={10}>
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: content.information
                        ? content.information.toString()
                        : 'Information om anläggningen saknas',
                    }}></Box>

                  <Box>
                    <Heading as="h2" size="sm" mb={7}>
                      Adress till lager i {content.name}
                    </Heading>
                    <Text>
                      {content.address
                        ? content.address
                        : 'Adress för anläggning saknas'}
                    </Text>
                    <Box
                      style={{
                        transition: '.1s color ease-in-out',
                        fontWeight: 'bold',
                      }}
                      _hover={{
                        color: colors.primary[900],
                      }}>
                      {content.phone ? (
                        <Text>
                          <a href={`tel:${content.phone}`}>{content.phone}</a>
                        </Text>
                      ) : (
                        <Text>Telefon för anläggning saknas</Text>
                      )}
                    </Box>
                  </Box>
                </SimpleGrid>
              </Flex>
              <Link
                target="_blank"
                href={`https://www.google.com/maps/dir/?api=1&destination=${content.address}&travelmode=driving`}>
                <Button variant="primary" size="lg" mt={6}>
                  Hitta hit
                </Button>
              </Link>
            </GridItem>
            <GridItem>
              <Box
                flex={{ base: 'none', md: 2 }}
                bg={colors.secondary[50]}
                h={{ base: 200, md: 200, lg: 300 }}
                pos="relative"
                borderRadius="1em"
                overflow="hidden"
                alignItems="center"
                d="flex"
                mt={{ base: '2em', md: 0 }}>
                {content.image ? (
                  <>
                    <Image
                      src={content.image._url}
                      alt={content.image.altText ?? `Bild på anlägggning`}
                      layout="fill"
                      className="facilityImage"
                      priority={true}
                      loading="eager"
                    />
                    <style jsx global>{`
                      .facilityImage {
                        object-fit: cover;
                      }
                    `}</style>
                  </>
                ) : (
                  <Text
                    textAlign="center"
                    flex="1"
                    color={colors.secondary[900]}>
                    Bild saknas
                  </Text>
                )}
              </Box>
            </GridItem>
          </Grid>
          <Text
            mt={{ base: 10, md: 20 }}
            mb={3}
            align="center"
            fontSize={14}
            color={colors.neutrals[500]}>
            {/* <ChevronDownIcon mr={1} w={10} h={6} />
            Scrolla ned för att boka förråd
            <ChevronDownIcon ml={1} w={10} h={6} /> */}
          </Text>
        </Container>
        {/* <Container
          h="100%"
          alignContent="left"
          maxW="7xl"
          alignItems="left"
          alignSelf="left">
          <Heading as="h2" size="sm" pb={2}>
            Hitta hit
          </Heading>
          {showMap && <chakra.img
            padding="0"
            display="block"
            boxSizing="border-box"
            border="none"
            position="relative"
            src={`https://maps.googleapis.com/maps/api/staticmap?center=${content.address}&markers=size:small|${content.address}&zoom=17&size=638x400&maptype=roadmap&key=AIzaSyAUhfA1W57pRUQhyNrwSc7SvFgBiZhPKFc&map_id=8052fe58b9f5174f"`}
          />}
        </Container> */}
      </Box>
    </>
  );
}

function Facility({
  products,
  facility,
  facilityContent,
  sharedPageProps,
}: FacilityProps): JSX.Element {
  const route = useRouter();
    console.log("PRODUCTS FOR THIS FACILITY", products);
  const filteredCampaigns = sharedPageProps?.campaigns.filter((campaign) => {
    return (
      (campaign.appliesToAll ||
        campaign.appliesToFacilityId?.includes(facility.id)) &&
      campaign.isActive
    );
  });
  const activeDiscounts: Discount[] = [];
  filteredCampaigns?.forEach((campaign) =>
    campaign?.discounts.forEach((discount) => activeDiscounts.push(discount))
  );
  return (
    <>
      <CampaignBanner
        campaigns={sharedPageProps?.campaigns}
        campaignLevel="Facility"
        id={facility?.id}
        title={facility?.name}
      />        
      <Box>
        {products?.length > 0 && (
          <Box
            p={{ base: '2em 5%', md: '5em 5%', xl: '5em 0' }}
            bg={colors.secondary[300]}>
            <Container maxW="7xl">
              <Box mx={0} mb={20}>
                <Heading as="h1" size="xl">
                      {!facilityContent.header
                        ? route?.asPath
                            ?.split('/anlaggning/')[1]
                            .replace('-', ' ')
                        : facilityContent.header}
                    </Heading>
              </Box>
              <Box>
                <SimpleGrid columns={respCols} spacing={respSpacing}>
                  {products.map((product: ProductDto) => {
                    return (
                      <ProductCard
                      key={product.id}
                      product={product}
                      facilityId={facility.id}
                      discounts={activeDiscounts}
                      />
                    );
                  })}
                </SimpleGrid>
              </Box>
                  <FacilityInfo
                    content={{ ...products, ...facility, ...facilityContent }}
                  />
              <Box>
                <Heading as="h2" size="md" mt={14} mb={4}>
                  {facilityContent.storeHeader}
                </Heading>
                <Box
                  dangerouslySetInnerHTML={{__html: facilityContent.storeInformation.toString()}}>
                </Box>
              </Box>              
            </Container>                
          </Box>
        )}
        {facilityContent?.blocks.length > 0 && (
          <>
          <HeadMeta {...facilityContent.meta} />
          <BlockRenderer blocks={facilityContent.blocks} />
          </>
        )}
      </Box>
    </>
  );
}

const respCols = { base: 1, sm: 1, md: 2, lg: 3, xl: 3 };
const respSpacing = { base: '3em' };

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const facility = await GetFacilityCore(params.slug.toString());
  const facilityContent = await GetFacilityUmbraco(params.slug.toString());
  const sharedPageProps = await getSharedPageProps();

  const products = await GetAvailableProductsForFacility(
    params.slug.toString()
  );

  return {
    revalidate: 20,
    props: {
      facility: facility,
      facilitySlug: params.slug,
      facilityContent,
      products,
      ...sharedPageProps,
    },
    notFound: facility === null || facility.name === "Gislaved",

  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const facilities = await GetFacilities();
  const paths = facilities.map((facility: FacilityDto) => ({
    params: { slug: facility.slug },
  }));
  return { paths, fallback: true };
};

export default Facility;
